import {
  addDoc,
  collection,
  CollectionReference,
  deleteDoc,
  doc,
  DocumentData,
  getDocs,
  onSnapshot,
  Query,
  query,
  serverTimestamp,
  updateDoc,
  where
} from "firebase/firestore";
import { useCallback, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { toast } from "sonner";
import { auth, db } from "../api/firebaseApi";
import { fetchFiles } from "../helpers/helpers";
import { DATABASE } from "../types/tables-data";
import { useUserRoleAndSubscription } from "./useUserRoleAndSubscription";
import { ToastManager } from "../services/toast/toastManager";

interface FirestoreHookOptions {
  shouldFetch?: boolean;
  listen?: boolean;
  fetchFiles?: boolean;
  withThumbnails?: boolean;
  useLawyerAccessMap?: boolean;
  usePublicAccess?: boolean;
  enableToast?: boolean;
  orderByField?: string;
  filterBy?: string;
  filterValue?: string;
}

function useFirestoreCollection<T>(
  collectionPath?: string,
  options: FirestoreHookOptions = {
    shouldFetch: true,
    listen: false,
    fetchFiles: false,
    withThumbnails: false,
    useLawyerAccessMap: false,
    usePublicAccess: true,
    enableToast: true,
    orderByField: "",
    filterBy: "",
    filterValue: ""
  }
) {
  const [documents, setDocuments] = useState<T[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isDocumentSaving, setIsDocumentSaving] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { isSuperAdmin, isAdminDashboard, role, lawyerId, lawyerPractice } =
    useUserRoleAndSubscription();

  const {
    shouldFetch = true,
    orderByField,
    listen,
    fetchFiles: shouldFetchFiles,
    withThumbnails,
    enableToast,
    usePublicAccess = true,
    useLawyerAccessMap = false
  } = options;

  // Monitor authentication state
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setIsAuthenticated(!!user);
    });
    return () => unsubscribe();
  }, []);

  const fetchDocFilesAndThumbnails = async (docData: any) => {
    const finalData = { ...docData, path: docData.path };

    if (finalData.filePath) {
      try {
        const docUrl = await fetchFiles([finalData.filePath]).then(
          (urls) => urls[0] || null
        );
        finalData.docUrl = docUrl ?? "";
        // Thumbnails are now provided by the backend API; skip client-side PDF rendering
      } catch (err) {
        Sentry.captureException(err);
      }
    }
    return finalData;
  };

  type FirestoreQuery = Query<DocumentData>;

  const getQueries = (colRef: CollectionReference): FirestoreQuery[] => {
    const queries = [];

    // Case 1: SuperAdmin in Admin Dashboard with Lawyer Access Map
    // Access: Only public documents
    if (useLawyerAccessMap && isSuperAdmin && isAdminDashboard) {
      queries.push(
        query(
          colRef,
          where("isDeleted", "==", false),
          where("access.public", "==", true)
        )
      );
    }

    // Case 2: SuperAdmin outside Admin Dashboard with Lawyer Access Map
    // Access: All non-deleted documents
    else if (useLawyerAccessMap && isSuperAdmin && !isAdminDashboard) {
      queries.push(query(colRef, where("isDeleted", "==", false)));
    }

    // Case 3: Regular user with Lawyer Access Map (or fallback superadmin)
    // Access: lawyerId match, practiceName match, or public
    else if (useLawyerAccessMap) {
      if (lawyerId) {
        queries.push(
          query(
            colRef,
            where("isDeleted", "==", false),
            where("access.public", "==", false),
            where("access.lawyerId", "==", lawyerId)
          )
        );
      }

      if (lawyerPractice) {
        queries.push(
          query(
            colRef,
            where("isDeleted", "==", false),
            where("access.public", "==", false),
            where("access.practiceName", "==", lawyerPractice)
          )
        );
      }

      if (usePublicAccess) {
        queries.push(
          query(
            colRef,
            where("isDeleted", "==", false),
            where("access.public", "==", true)
          )
        );
      }
    }

    // Case 4: No Lawyer Access Map
    // Access: Only SuperAdmin gets full access, others get nothing
    else if (isSuperAdmin) {
      queries.push(query(colRef, where("isDeleted", "==", false)));
    }

    // Case 5: Regular user without Lawyer Access Map
    // Access: No access (empty)
    // This is implicit by falling through

    return queries;
  };

  const handleError = (err: any) => {
    // Don't log permission errors during logout
    if (err?.code === "permission-denied" && !isAuthenticated) {
      return;
    }
    Sentry.captureException(err);
    setError(err);
    setLoading(false);
  };

  const fetchData = async () => {
    if (!collectionPath || !isAuthenticated) return;
    const colRef = collection(db, collectionPath);
    const queries = getQueries(colRef);
    setLoading(true);
    try {
      const snapshots = await Promise.all(queries.map((q) => getDocs(q)));
      const mergedMap = new Map<string, any>();
      snapshots.forEach((snapshot) => {
        snapshot.docs.forEach((doc) => {
          mergedMap.set(doc.id, doc);
        });
      });
      const dataPromises = Array.from(mergedMap.values()).map(async (doc) => {
        let docData = { id: doc.id, path: doc.ref.path, ...doc.data() };
        if (shouldFetchFiles) {
          docData = await fetchDocFilesAndThumbnails(docData);
        }
        return docData;
      });
      const data = (await Promise.all(dataPromises)) as T[];
      setDocuments(data);
      setLoading(false);
      setInitialLoadDone(true);
    } catch (err) {
      handleError(err);
    }
  };

  useEffect(() => {
    if (!shouldFetch || !collectionPath || !isAuthenticated) {
      return () => {};
    }

    const colRef = collection(db, collectionPath);
    const queries = getQueries(colRef);

    let unsubscribe: (() => void) | undefined;

    if (listen) {
      const unsubscribes: Array<() => void> = [];
      const docsMaps = queries.map(() => new Map<string, any>());
      const hasInitialDataLoaded = new Array(queries.length).fill(false);
      setLoading(true);
      setInitialLoadDone(false);

      queries.forEach((q, index) => {
        unsubscribes.push(
          onSnapshot(
            q,
            async (snapshot) => {
              const docPromises = snapshot.docChanges().map(async (change) => {
                if (change.type === "removed" || change.doc.data().isDeleted) {
                  docsMaps[index].delete(change.doc.id);
                } else {
                  let docData = {
                    id: change.doc.id,
                    path: change.doc.ref.path,
                    ...change.doc.data()
                  };
                  if (shouldFetchFiles) {
                    docData = await fetchDocFilesAndThumbnails(docData);
                  }
                  docsMaps[index].set(change.doc.id, docData);
                }
              });

              await Promise.all(docPromises);

              // Check if this is the initial snapshot
              if (!hasInitialDataLoaded[index]) {
                hasInitialDataLoaded[index] = true; // Mark initial data as loaded
              }

              // Only update state if ALL queries have returned their first snapshot
              if (hasInitialDataLoaded.every((loaded) => loaded)) {
                const mergedMap = new Map<string, any>();
                docsMaps.forEach((map) => {
                  map.forEach((value, key) => {
                    mergedMap.set(key, value);
                  });
                });
                const updatedData = Array.from(mergedMap.values());
                setDocuments(updatedData);
                setLoading(false);
                if (!initialLoadDone) {
                  setInitialLoadDone(true);
                }
              }
            },
            handleError
          )
        );
      });

      unsubscribe = () => unsubscribes.forEach((unsub) => unsub());
    } else {
      // One-time fetch
      fetchData();
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [
    collectionPath,
    listen,
    orderByField,
    options.useLawyerAccessMap,
    lawyerId,
    lawyerPractice,
    role,
    isAuthenticated
  ]);

  const saveDocument = useCallback(
    async (newDoc: Partial<any>): Promise<string | undefined> => {
      if (!collectionPath) {
        Sentry.captureException(
          new Error("saveDocument: No collectionPath provided")
        );
        toast.error("Error", {
          description: "No collection path provided. Unable to save document.",
          duration: 5000
        });
        return undefined;
      }

      setIsDocumentSaving(true);
      try {
        const colRef = collection(db, collectionPath);

        if (options.useLawyerAccessMap) {
          if (!isSuperAdmin || (isSuperAdmin && !isAdminDashboard)) {
            newDoc.access = {
              public: false,
              lawyerId: lawyerId || undefined,
              practiceName: lawyerPractice || undefined
            };
          } else if (isSuperAdmin && isAdminDashboard) {
            newDoc.access = {
              public: true
            };
          }
        }

        if (newDoc.id) {
          const docRef = doc(db, collectionPath, newDoc.id);
          await updateDoc(docRef, {
            ...newDoc,
            updated_at: serverTimestamp()
          });
          if (enableToast) {
            const segment = Object.values(DATABASE).includes(
              collectionPath as DATABASE
            )
              ? (collectionPath as DATABASE)
              : (collectionPath.split("/")[2] as DATABASE);
            ToastManager.getInstance().showToast(segment, "success", "updated");
          }
          return newDoc.id;
        }

        const docRef = await addDoc(colRef, {
          ...newDoc,
          created_at: serverTimestamp(),
          isDeleted: false
        });

        await updateDoc(docRef, {
          id: docRef.id
        });

        if (enableToast) {
          const segment = Object.values(DATABASE).includes(
            collectionPath as DATABASE
          )
            ? (collectionPath as DATABASE)
            : (collectionPath.split("/")[2] as DATABASE);
          ToastManager.getInstance().showToast(segment, "success", "added");
        }
        return docRef.id;
      } catch (err: any) {
        Sentry.captureException(err);
        toast.error("Error saving document", {
          description: err.message || "An unknown error occurred.",
          duration: 5000
        });
        return undefined;
      } finally {
        setIsDocumentSaving(false);
      }
    },
    [
      collectionPath,
      lawyerId,
      lawyerPractice,
      enableToast,
      options.useLawyerAccessMap
    ]
  );

  const deleteDocument = useCallback(
    async (id: string) => {
      if (!collectionPath) {
        Sentry.captureException(
          new Error("deleteDocument: No collectionPath provided")
        );
        toast.error("Error", {
          description:
            "No collection path provided. Unable to delete document.",
          duration: 5000
        });
        return;
      }
      try {
        const docRef = doc(db, collectionPath, id);
        await updateDoc(docRef, { isDeleted: true });
        const segment = Object.values(DATABASE).includes(
          collectionPath as DATABASE
        )
          ? (collectionPath as DATABASE)
          : (collectionPath.split("/")[2] as DATABASE);
        ToastManager.getInstance().showToast(segment, "success", "deleted");
      } catch (err: any) {
        Sentry.captureException(err);
        toast.error("Error deleting document", {
          description: err.message || "An unknown error occurred.",
          duration: 5000
        });
      }
    },
    [collectionPath]
  );

  const deleteAll = useCallback(
    async (hardDelete = false) => {
      if (!collectionPath) {
        Sentry.captureException(
          new Error("deleteAll: No collectionPath provided")
        );
        toast.error("Error", {
          description:
            "No collection path provided. Unable to delete all documents.",
          duration: 5000
        });
        return;
      }
      try {
        const allDocs: any[] = [];
        if (hardDelete) {
          // Fetch all docs in the collection, no filters
          const colRef = collection(db, collectionPath);
          const snapshot = await getDocs(colRef);
          snapshot.docs.forEach((docSnapshot) => {
            allDocs.push({
              id: docSnapshot.id,
              ref: docSnapshot.ref
            });
          });
        } else {
          // Use filtered queries
          const colRef = collection(db, collectionPath);
          const queries = getQueries(colRef);
          const snapshots = await Promise.all(queries.map((q) => getDocs(q)));
          snapshots.forEach((snapshot) => {
            snapshot.docs.forEach((docSnapshot) => {
              allDocs.push({
                id: docSnapshot.id,
                ref: docSnapshot.ref
              });
            });
          });
        }

        if (hardDelete) {
          // Hard delete - physically remove documents
          const deletePromises = allDocs.map((docData) =>
            deleteDoc(docData.ref)
          );
          await Promise.all(deletePromises);
        } else {
          // Soft delete - set isDeleted: true
          const updatePromises = allDocs.map((docData) =>
            updateDoc(docData.ref, { isDeleted: true })
          );
          await Promise.all(updatePromises);
        }

        if (enableToast) {
          const segment = Object.values(DATABASE).includes(
            collectionPath as DATABASE
          )
            ? (collectionPath as DATABASE)
            : (collectionPath.split("/")[2] as DATABASE);
          ToastManager.getInstance().showToast(segment, "success", "deleted");
        }
      } catch (err: any) {
        Sentry.captureException(err);
        toast.error("Error deleting all documents", {
          description: err.message || "An unknown error occurred.",
          duration: 5000
        });
      }
    },
    [collectionPath]
  );

  return {
    saveDocument,
    deleteDocument,
    deleteAll,
    isDocumentSaving,
    documents,
    loading,
    error,
    initialLoadDone,
    fetchData
  };
}

export default useFirestoreCollection;
